// src/pages/team/TeamAuditLogPage.tsx

import { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useTeam, useI18n } from '@react/hooks';
import {
  Card,
  CardContent,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  ErrorAlert,
} from '@react/components';
import { auditActionMap, auditTargetTypes, formatDateTime } from '@/features/team/logic';
import { TeamSectionNav } from '@/features/team/TeamSectionNav';

export const TeamAuditLogPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useTeam();
  const { t, locale } = useI18n();

  const [allLogs, setAllLogs] = useState<any[]>([]);
  const [filterTargetType, setFilterTargetType] = useState('all');

  // The generated ListTeamLogs takes no filter params — filter client-side.
  const loadLogs = useCallback(async () => {
    if (!id) return;
    try {
      const res = await store.ListTeamLogs(id);
      setAllLogs(res.logs?.logs ?? []);
    } catch { /* store handles */ }
  }, [id, store]);

  const logs = filterTargetType === 'all'
    ? allLogs
    : allLogs.filter((log) => log.target_type === filterTargetType);

  useEffect(() => {
    if (id) store.GetTeam(id);
    loadLogs();
  }, [id, loadLogs]);

  const teamName = (store.currentItem as any)?.name || t('team.title');

  return (
    <div className="space-y-6">
      <nav className="text-sm">
        <Link to="/teams" className="text-muted-foreground hover:text-foreground">{t('team.list')}</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to={`/teams/${id}`} className="text-muted-foreground hover:text-foreground">{teamName}</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{t('team.nav.audit')}</span>
      </nav>
      <TeamSectionNav teamId={id ?? ''} group="people" active="audit" />


      {/* The tab already names this page; the heading stays for screen readers. */}
      <h1 className="sr-only">{t('team.auditTitle')}</h1>

      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-foreground">{t('team.auditFilterLabel')}:</label>
            <div className="flex gap-1">
              {auditTargetTypes.map((type) => (
                <button
                  key={type}
                  onClick={() => setFilterTargetType(type)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    filterTargetType === type
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('common.action')}</TableHead>
              <TableHead>{t('common.target')}</TableHead>
              <TableHead>{t('common.actor')}</TableHead>
              <TableHead>{t('team.details')}</TableHead>
              <TableHead>{t('common.timestamp')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">{t('common.loading')}</TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableEmpty colSpan={5} message={t('team.noAuditLogs')} />
            ) : (
              logs.map((log: any, i: number) => (
                <TableRow key={log.entity?.id || i}>
                  <TableCell>
                    <Badge variant={(auditActionMap[log.action] || 'secondary') as any} size="sm">
                      {log.action || '--'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div>
                      <span className="text-xs text-muted-foreground">{log.target_type}</span>
                      {log.target?.id && (
                        <p className="text-xs font-mono text-muted-foreground">{log.target.id}</p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{log.actor?.display_name || log.actor?.name || '--'}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-xs text-muted-foreground truncate max-w-xs block">
                      {log.new_value ? `${log.old_value || '(empty)'} -> ${log.new_value}` : '--'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground">{formatDateTime(log.timestamp, locale)}</span>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
});

export default TeamAuditLogPage;
